import { z } from 'zod'

import { apiClient, getData, getPage } from '@/api/client'
import type { Meta } from '@/api/envelope'
import type { components } from '@/api/schema'
import { pageTotal } from '@/features/market/api/marketMappers'
import { companyMediaUrl } from '@/lib/company-logos'
import {
  brokerByStockSchema,
  brokerDetailSchema,
  brokerDisplayName,
  brokerListRowSchema,
  brokerOverviewSchema,
  brokerPeerSchema,
  brokerPeriodSchema,
  brokerReviewSchema,
  brokerReviewsSchema,
  brokerStockFlowSchema,
  brokersBySectorSchema,
  brokerTradeSchema,
  busyNameSchema,
  compareSchema,
  formatBrokerCodes,
  largeHoldingsSchema,
  largeTradeLeaderSchema,
  largeTradesSchema,
  pvRangeSchema,
  reviewDraftSchema,
  sectorBrokersSchema,
  sectorFlowSchema,
  stockDisplayName,
  stocksInCommonSchema,
  stocksTradedMostSchema,
  windowedTablePageSchema,
  type BrokerByStock,
  type BrokerDateRange,
  type BrokerDetail,
  type BrokerListQuery,
  type BrokerListRow,
  type BrokerOverview,
  type BrokerPeer,
  type BrokerPeriod,
  type BrokerReview,
  type BrokerReviews,
  type BrokerReviewsQuery,
  type BrokersBySector,
  type BrokerStockFlow,
  type BrokerStockSort,
  type BrokerStocksQuery,
  type BrokerTrade,
  type BrokerTradesQuery,
  type BusyName,
  type BusyNamesQuery,
  type Compare,
  type LargeHoldings,
  type LargeHoldingsQuery,
  type LargeTradeLeader,
  type LargeTradeLeadersQuery,
  type LargeTrades,
  type LargeTradesQuery,
  type ListWindow,
  type PVRange,
  type PVRangeQuery,
  type ReviewDraft,
  type SectorBrokers,
  type SectorFlow,
  type StocksInCommon,
  type StocksTradedMost,
  type WindowedTablePage,
} from '@/schemas/broker'
import { tablePageSchema, type PageSize, type TablePage } from '@/schemas/table'

/**
 * The Broker Chirfaar seam: nineteen endpoints in, app types out.
 *
 * Was fifteen until `large-holdings` and `pv-range` joined it. Both were live
 * in mm-be and in this app's own generated `schema.d.ts` the whole time; what
 * kept them out was a stale comment in `src/routes/brokers.ts` claiming they
 * had no backend. See that file. `stocks-traded-most` made eighteen: it is
 * new in mm-be, served for the Overview card of the same name. `by-sector`
 * made nineteen, new in mm-be for Sector flow's "Broker by sector" grid.
 *
 * Structured like src/features/floorsheet/api/ because it is the same kind of
 * surface — a multi-tab, sortable, offset-paged market-data read. The one
 * difference that matters: floorsheet aggregates client-side over the raw
 * trade list, while these endpoints do the aggregation and the paging
 * themselves. So paging here is a REQUEST parameter, not a slice taken
 * afterwards, and `sliceTablePage` would be wrong — it would page a page.
 *
 * Wire types come from `@/api/schema`, never restated. That is the compile-time
 * half of the contract check: a regenerated `schema.d.ts` that renames a field
 * breaks the build in this file, which is exactly where it should break. The
 * runtime half is the Zod parse every mapper's result goes through before it
 * leaves the module.
 */

type Wire<K extends keyof components['schemas']> = components['schemas'][K]

const listPageSchema = tablePageSchema(brokerListRowSchema)
const stockFlowPageSchema = tablePageSchema(brokerStockFlowSchema)
const tradePageSchema = tablePageSchema(brokerTradeSchema)

/**
 * Busy names is the ONE paged broker endpoint whose `meta` reports the window,
 * so it is the one page schema that carries it. The other three
 * (`/brokers/`, `/brokers/{code}/stocks/`, `/brokers/{code}/floorsheet/`) end
 * at `asdict(page)` — `api/v5/brokers/views.py:70`, `:185`, `:195` — and stay
 * plain `TablePage`s rather than gaining a `window` that would always be null.
 */
const busyNamePageSchema = windowedTablePageSchema(busyNameSchema)

/**
 * The window fields busy-names merges into its list meta, in WIRE spelling.
 *
 * Kept here rather than in `@/schemas/broker` for the house reason that file
 * states at `src/schemas/auth.ts:63`: a schema module holds wire-free shapes,
 * and `as_of` is the wire's word. `Meta` does not declare these keys — the
 * OpenAPI document types every list's meta as `{ count } & Record<string,
 * unknown>` — so they are parsed out rather than cast in.
 */
const listWindowMetaSchema = z.object({
  period: brokerPeriodSchema,
  as_of: z.string().min(1).nullable(),
})

/**
 * One page of a SERVER-paged list, in the shape `DataTable` and the pager read.
 *
 * `page` and `pageSize` are echoed from the request rather than read back out
 * of `meta`: they are the numbers in the URL, and the pill marked 3 and
 * `?page=3` can never be allowed to drift. `total` does come from `meta` —
 * only the backend knows how many rows exist beyond this page — and falls back
 * to the row count so a `meta` without it renders one honest page instead of
 * throwing.
 */
/**
 * R5: five fields the backend always sets in lockstep with a sibling —
 * `busiest_turnover` beside `busiest_code`, `most_in_net`/`most_out_net`
 * beside their sector names, `top_pair_count` beside the pair's two codes
 * (`overview.py:87-89`, `sectorflow.py:75-78`, `largetrades.py:92-94` each
 * assign the whole group from one row or tuple, never one field alone) — used
 * to fall back to `?? '0'` / `?? 0` inside this module. That default can never
 * fire today, so it was a no-op UNTIL the day it wasn't: a future backend
 * change that broke the lockstep would turn a real gap into an indistinguishable,
 * real-looking zero instead of surfacing at all. This throws instead, the same
 * way `marketService.ts`/`overviewService.ts` already do for their own
 * "the backend promised these travel together" invariants — loud beats a
 * silently wrong number.
 */
const required = <T>(value: T | null | undefined, field: string): T => {
  if (value === null || value === undefined) {
    throw new Error(`${field} was missing alongside a value that implies it must be present`)
  }
  return value
}

/**
 * The ceiling on `/brokers/{code}/stocks/` — `page_size` is validated at
 * `max_value=200` (mm-be api/v5/brokers/serializers.py:40) and clamped to the
 * same `MAX_PAGE_SIZE` in `services/common/paging.py`, so asking for more is a
 * 400 rather than a bigger page. Used by `allStocks`, which walks the list.
 */
const ALL_STOCKS_PAGE_SIZE = 200

const serverPage = (
  rows: readonly unknown[],
  meta: Meta,
  query: { page: number; pageSize: PageSize },
) => ({
  rows,
  page: query.page,
  pageSize: query.pageSize,
  total: pageTotal(meta, rows.length),
})

// ── Mappers ──────────────────────────────────────────────────────────────────
//
// Exported so each one is testable on a fixture without a request. They map
// only: every value they produce is checked by the Zod schema at the call site,
// so a mapper never re-validates what the schema is about to.

/**
 * The period / as_of / source triple, lifted into one object.
 *
 * `source` is carried through, never dropped. It is the only way a screen can
 * tell that a `1m` request came back with one session in it because the
 * nightly summary lagged — see `isPartialWindow` in @/schemas/broker.
 */
export const mapWindow = (wire: { period: string; as_of: string | null; source: string }) => ({
  period: wire.period,
  asOf: wire.as_of,
  source: wire.source,
})

/**
 * The window a paged list reports, read off the envelope's `meta`, or null
 * when this endpoint does not report one.
 *
 * Null rather than a throw for the three endpoints whose meta is paging and
 * nothing else, and equally for a busy-names response that one day arrives
 * without its extras: a table must not lose its rows because it could not
 * caption them. `source` is not read because it is not sent — see
 * `listWindowSchema` in `@/schemas/broker` and the report's backend section.
 *
 * Takes the meta as it ARRIVED, not as `Meta`: that type declares `count`,
 * the cursor pair and the four offset-paging keys by hand (see
 * `src/api/envelope.ts` — the OpenAPI document types every list's meta as
 * `{ count } & Record<string, unknown>`, so `pnpm gen:api` cannot produce
 * them), and `as_of`/`period` are not among them. Parsing them out of the raw
 * value is honest about that; reading them off a type that denies they exist
 * would need a cast.
 */
export const mapListWindow = (meta: unknown): ListWindow | null => {
  const parsed = listWindowMetaSchema.safeParse(meta)
  return parsed.success ? { period: parsed.data.period, asOf: parsed.data.as_of } : null
}

export const mapStockFlow = (wire: Wire<'BrokerStockFlow'>) => ({
  symbol: wire.symbol,
  name: stockDisplayName(wire.symbol, wire.name),
  boughtValue: wire.bought_value,
  soldValue: wire.sold_value,
  netValue: wire.net_value,
  boughtQty: wire.bought_qty,
  soldQty: wire.sold_qty,
  netQty: wire.net_qty,
  trades: wire.trades,
  avgBuyRate: wire.avg_buy_rate,
  avgSellRate: wire.avg_sell_rate,
  lean: wire.lean,
})

export const mapRankRow = (wire: Wire<'_BrokerRankRow'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  value: wire.value,
})

export const mapOverview = (wire: Wire<'BrokerOverview'>) => ({
  window: mapWindow(wire),
  turnover: wire.turnover,
  trades: wire.trades,
  companies: wire.companies,
  brokersTrading: wire.brokers_trading,
  ofBrokers: wire.of_brokers,
  // Three parallel nullable fields on the wire, one nullable object here:
  // the code is what makes the busiest broker exist, so a turnover that
  // arrived null beside a present code is a zero, not a missing house.
  busiest:
    wire.busiest_code === null
      ? null
      : {
          code: wire.busiest_code,
          name: brokerDisplayName(wire.busiest_code, wire.busiest_name),
          turnover: required(wire.busiest_turnover, 'busiest_turnover'),
        },
  topBought: wire.top_bought.map(mapRankRow),
  topSold: wire.top_sold.map(mapRankRow),
})

/**
 * `name` folds mm-be's two spellings of "no company on file" — `null`, and
 * `""` for a listed symbol whose company row has no name (common on dev) —
 * into one null. Not `stockDisplayName`: the chip already prints the symbol,
 * so a name that fell back to it would only repeat it in the tooltip.
 */
export const mapTradedStock = (wire: Wire<'_TradedStockRow'>) => ({
  symbol: wire.symbol,
  name: wire.name === null || wire.name.trim().length === 0 ? null : wire.name,
  valueTraded: wire.value_traded,
  sharePct: wire.share_pct,
  trades: wire.trades,
  brokers: wire.brokers,
})

export const mapStocksTradedMost = (wire: Wire<'StocksTradedMost'>) => ({
  window: mapWindow(wire),
  marketValueTraded: wire.market_value_traded,
  stocks: wire.stocks.map(mapTradedStock),
})

export const mapListRow = (wire: Wire<'BrokerListRow'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  rating: wire.rating,
  turnover: wire.turnover,
  netTurnover: wire.net_turnover,
  lean: wire.lean,
})

export const mapDetail = (wire: Wire<'BrokerDetail'>) => ({
  window: mapWindow(wire),
  header: {
    code: wire.header.code,
    name: brokerDisplayName(wire.header.code, wire.header.name),
    // A RELATIVE media path on the wire (`broker/logo/<hash>.png`) — the
    // backend never prefixes it. Made absolute against the logo CDN's base,
    // which serves it (see `companyMediaUrl`).
    logoUrl: wire.header.logo_url === null ? null : companyMediaUrl(wire.header.logo_url),
    rating: wire.header.rating,
    reviewCount: wire.header.review_count,
  },
  kpis: {
    turnover: wire.kpis.turnover,
    trades: wire.kpis.trades,
    marketSharePct: wire.kpis.market_share_pct,
    buySellRatio: wire.kpis.buy_sell_ratio,
  },
  flow: {
    bought: wire.flow.bought,
    sold: wire.flow.sold,
    net: wire.flow.net,
    boughtPct: wire.flow.bought_pct,
    soldPct: wire.flow.sold_pct,
  },
  tradeSizes: wire.trade_sizes,
  vsMarket: {
    turnoverMultiple: wire.vs_market.turnover_multiple,
    tradesMultiple: wire.vs_market.trades_multiple,
    buySellRatio: wire.vs_market.buy_sell_ratio,
    marketAvgRatio: wire.vs_market.market_avg_ratio,
    avgTrade: wire.vs_market.avg_trade,
    marketAvgTrade: wire.vs_market.market_avg_trade,
  },
  style: {
    styleLabel: wire.style.style_label,
    bigTradePct: wire.style.big_trade_pct,
    busiestSector: wire.style.busiest_sector,
    stocksOnlyBought: wire.style.stocks_only_bought,
    stocksTraded: wire.style.stocks_traded,
    sharesBought: wire.style.shares_bought,
    sharesSold: wire.style.shares_sold,
    netShares: wire.style.net_shares,
    mostTradedStock: wire.style.most_traded_stock,
  },
  topBought: wire.top_bought.map(mapStockFlow),
  topSold: wire.top_sold.map(mapStockFlow),
  topNet: wire.top_net.map(mapStockFlow),
  activityTop: wire.activity_top.map(mapStockFlow),
  sharesHeld: wire.shares_held.map(mapStockFlow),
  moreSold: wire.more_sold.map(mapStockFlow),
})

export const mapPeer = (wire: Wire<'BrokerPeer'>) => ({
  rank: wire.rank,
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  turnover: wire.turnover,
  marketSharePct: wire.market_share_pct,
  buySellRatio: wire.buy_sell_ratio,
  isCurrent: wire.is_current,
})

export const mapTrade = (wire: Wire<'BrokerTrade'>) => ({
  contractNo: wire.contract_no,
  symbol: wire.symbol,
  side: wire.side,
  /**
   * Same phantom-`""` guard as `mapTradeParty` below, and the same shape of
   * hazard: `brokerTradeSchema.counterparty` is `z.string().min(1).nullable()`,
   * and `services/broker/stocks.py:81` passes `t.seller`/`t.buyer` through with
   * no filter at all — unlike the four places mm-be does guard falsy codes. An
   * empty code on one contract would fail the page's parse.
   */
  counterparty: wire.counterparty === '' ? null : wire.counterparty,
  quantity: wire.quantity,
  rate: wire.rate,
  amount: wire.amount,
  tradeTime: wire.trade_time,
})

export const mapReview = (wire: Wire<'Review'>) => ({
  id: wire.id,
  rating: wire.rating,
  text: wire.text,
  author: wire.author,
  initials: wire.initials,
  verified: wire.verified,
  createdOn: wire.created_on,
  // Not in the generated wire type yet (`asks/broker-reviews.md`): read off the raw object, and
  // only ever `true` when the server says so.
  mine: (wire as { mine?: unknown }).mine === true,
})

export const mapReviews = (wire: Wire<'BrokerReviews'>) => ({
  code: wire.code,
  average: wire.average,
  total: wire.total,
  histogram: wire.histogram.map((bucket) => ({
    star: bucket.star,
    count: bucket.count,
    pct: bucket.pct,
  })),
  page: wire.page,
  pageSize: wire.page_size,
  totalPages: wire.total_pages,
  reviews: wire.reviews.map(mapReview),
})

export const mapStockBrokerRow = (wire: Wire<'_StockBrokerRow'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  trades: wire.trades,
  shares: wire.shares,
  amount: wire.amount,
  avgPrice: wire.avg_price,
  pctOfTrades: wire.pct_of_trades,
  pctOfSide: wire.pct_of_side,
  rating: wire.rating,
})

export const mapStockHolderRow = (wire: Wire<'_StockHolderRow'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  shares: wire.shares,
  amount: wire.amount,
  avgBuyPrice: wire.avg_buy_price,
  avgSellPrice: wire.avg_sell_price,
})

/**
 * `name` reads through `?? null` because this row goes on to `lenientList`: a row that lacks
 * its name must reach the schema and be dropped there, not throw here and blank the whole read.
 */
export const mapStockNetRow = (wire: Wire<'_StockNetRow'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name ?? null),
  boughtShares: wire.bought_shares,
  soldShares: wire.sold_shares,
  netShares: wire.net_shares,
  netAmount: wire.net_amount,
  rating: wire.rating,
})

/**
 * `BrokerByStock` as this mapper must survive it: a deploy older than mm-be
 * 5f4ed8f sends no `totals` (see "By stock" in `schemas/broker.ts`), and one
 * older than the net lists sends no `net_buyers` / `net_sellers`. The
 * generated type is today's contract, and reading `wire.totals.*` off it would
 * be a TypeError there, failing Brokers › By stock before the schema could
 * call the block missing.
 */
type ByStockWire = Omit<Wire<'BrokerByStock'>, 'totals' | 'net_buyers' | 'net_sellers'> & {
  totals?: Wire<'BrokerByStock'>['totals'] | null
  net_buyers?: Wire<'BrokerByStock'>['net_buyers'] | null
  net_sellers?: Wire<'BrokerByStock'>['net_sellers'] | null
}

export const mapByStock = (wire: ByStockWire) => {
  const totals = wire.totals ?? null
  return {
    window: { ...mapWindow(wire), fromDate: wire.from_date ?? null, toDate: wire.to_date ?? null },
    symbol: wire.symbol,
    name: stockDisplayName(wire.symbol, wire.name),
    ltp: wire.ltp,
    change: wire.change,
    changePercent: wire.change_percent,
    totals:
      totals === null
        ? null
        : {
            boughtShares: totals.bought_shares,
            soldShares: totals.sold_shares,
            boughtAmount: totals.bought_amount,
            soldAmount: totals.sold_amount,
            brokers: totals.brokers,
          },
    mostBought: wire.most_bought.map(mapStockBrokerRow),
    mostSold: wire.most_sold.map(mapStockBrokerRow),
    leftHolding: wire.left_holding.map(mapStockHolderRow),
    netBuyers: (wire.net_buyers ?? []).map(mapStockNetRow),
    netSellers: (wire.net_sellers ?? []).map(mapStockNetRow),
  }
}

export const mapBusyName = (wire: Wire<'BusyName'>) => ({
  symbol: wire.symbol,
  name: stockDisplayName(wire.symbol, wire.name),
  pctOfSize: wire.pct_of_size,
  changePercent: wire.change_percent,
  ltp: wire.ltp,
})

export const mapSectorFlow = (wire: Wire<'SectorFlow'>) => ({
  window: mapWindow(wire),
  mostIn:
    wire.most_in_sector === null
      ? null
      : { sector: wire.most_in_sector, net: required(wire.most_in_net, 'most_in_net') },
  mostOut:
    wire.most_out_sector === null
      ? null
      : { sector: wire.most_out_sector, net: required(wire.most_out_net, 'most_out_net') },
  sectors: wire.sectors.map((row) => ({
    sector: row.sector,
    net: row.net,
    bought: row.bought,
    sold: row.sold,
    brokers: row.brokers,
    stocks: row.stocks,
    buyPressurePct: row.buy_pressure_pct,
  })),
})

export const mapSectorBrokers = (wire: Wire<'SectorBrokers'>) => ({
  window: mapWindow(wire),
  sector: wire.sector,
  netBuyers: wire.net_buyers,
  totalBrokers: wire.total_brokers,
  brokers: wire.brokers.map((row) => ({
    code: row.code,
    name: brokerDisplayName(row.code, row.name),
    net: row.net,
    bought: row.bought,
    sold: row.sold,
  })),
})

/**
 * The Broker-by-sector grid. `name` goes through `brokerDisplayName` like every
 * other broker row, and each cell's `net` passes through as sent: a null stays
 * null ("did not trade this sector"), never `0`.
 */
export const mapBrokersBySector = (wire: Wire<'BrokersBySector'>) => ({
  window: mapWindow(wire),
  sectors: wire.sectors,
  brokers: wire.brokers.map((row) => ({
    code: row.code,
    name: brokerDisplayName(row.code, row.name),
    tradedValue: row.traded_value,
    net: row.net,
    sectorsTraded: row.sectors_traded,
    cells: row.cells.map((cell) => ({ sector: cell.sector, net: cell.net })),
  })),
})

/**
 * A side of a large trade. Null when NEPSE did not publish that counterparty.
 *
 * AN EMPTY STRING IS ALSO "NOT PUBLISHED". mm-be treats a falsy broker code as
 * a phantom in four separate places and names it — `listing.py:44` (`# phantom
 * "" broker bucket from a loose loader`), `overview.py:38`, `repository.py:322`
 * and `:463` — and `largetrades.py:80` filters `if k[0] and k[1]` before
 * picking the top pair, but NOT before returning the rows. Checking `=== null`
 * alone let a `''` through as `{ code: '', name: 'Broker ' }`, which then fails
 * `largeTradeSchema`'s `code: z.string().min(1)` — and because `LargeTrades` is
 * ONE payload, the summary tiles and the trade list parse together, so a single
 * unpublished counterparty anywhere on the current page took down the entire
 * screen with an opaque parse error rather than blanking one cell. (The `name`
 * side passed: `brokerDisplayName('', null)` returns `"Broker "`, which clears
 * `.min(1)`. The failure was on `code` alone.)
 *
 * Not observed in a live payload — the staging dataset is four clean brokers —
 * so this mirrors a guard mm-be already applies rather than fixing a reproduced
 * crash. `TradesTable` already renders a null party as "—".
 */
const mapTradeParty = (code: string | null, name: string | null) =>
  code === null || code === '' ? null : { code, name: brokerDisplayName(code, name) }

export const mapLargeTrade = (wire: Wire<'_LargeTrade'>) => ({
  contractNo: wire.contract_no,
  symbol: wire.symbol,
  band: wire.band,
  buyer: mapTradeParty(wire.buyer_code, wire.buyer_name),
  seller: mapTradeParty(wire.seller_code, wire.seller_name),
  shares: wire.shares,
  rate: wire.rate,
  amount: wire.amount,
  tradeTime: wire.trade_time,
})

export const mapLargeTrades = (wire: Wire<'LargeTrades'>) => ({
  asOf: wire.as_of,
  size: wire.size,
  count: wire.count,
  totalValue: wire.total_value,
  veryLarge: wire.very_large,
  large: wire.large,
  avgSize: wire.avg_size,
  // A pair needs both sides to be a pair at all.
  topPair:
    wire.top_pair_buyer === null || wire.top_pair_seller === null
      ? null
      : {
          buyer: wire.top_pair_buyer,
          seller: wire.top_pair_seller,
          count: required(wire.top_pair_count, 'top_pair_count'),
        },
  page: wire.page,
  pageSize: wire.page_size,
  total: wire.total,
  totalPages: wire.total_pages,
  trades: wire.trades.map(mapLargeTrade),
})

export const mapLargeTradeLeader = (wire: Wire<'LargeTradeLeader'>) => ({
  code: wire.code,
  name: brokerDisplayName(wire.code, wire.name),
  trades: wire.trades,
  value: wire.value,
  lean: wire.lean,
})

export const mapLargeHolding = (wire: Wire<'_LargeHolding'>) => ({
  symbol: wire.symbol,
  name: stockDisplayName(wire.symbol, wire.name),
  code: wire.code,
  brokerName: brokerDisplayName(wire.code, wire.broker_name),
  netQty: wire.net_qty,
  avgRate: wire.avg_rate,
  pctOfCapital: wire.pct_of_capital,
})

/**
 * Paging stays INSIDE the result, exactly as it arrives — `LargeHoldingsView`
 * returns a plain `Response`, so there is no envelope `meta` and `serverPage`
 * would have nothing to read. Same shape as `mapLargeTrades`.
 */
export const mapLargeHoldings = (wire: Wire<'LargeHoldings'>) => ({
  window: mapWindow(wire),
  sector: wire.sector,
  page: wire.page,
  pageSize: wire.page_size,
  total: wire.total,
  totalPages: wire.total_pages,
  holdings: wire.holdings.map(mapLargeHolding),
})

export const mapPVBucket = (wire: Wire<'_PVBucket'>) => ({
  low: wire.low,
  high: wire.high,
  quantity: wire.quantity,
})

/**
 * The three `busiest_*` fields become one nullable object, the same trade the
 * busiest broker gets in `mapOverview`: the bucket is what makes the range
 * exist, so `busiest_high` missing beside a present `busiest_low` throws rather
 * than printing "306 to 0". `busiest_pct` stays a nullable FIELD of that
 * object, not a reason for it to be absent — `pvrange.py` leaves the percentage
 * `None` whenever the window's total quantity is zero, which is a real state
 * for a bucket list of empty buckets.
 */
export const mapPVRange = (wire: Wire<'PVRange'>) => ({
  window: mapWindow(wire),
  symbol: wire.symbol,
  name: stockDisplayName(wire.symbol, wire.name),
  buyer: wire.buyer,
  seller: wire.seller,
  totalTraded: wire.total_traded,
  busiest:
    wire.busiest_low === null
      ? null
      : {
          low: wire.busiest_low,
          high: required(wire.busiest_high, 'busiest_high'),
          pct: wire.busiest_pct,
        },
  buckets: wire.buckets.map(mapPVBucket),
})

export const mapCompare = (wire: Wire<'Compare'>) => ({
  window: mapWindow(wire),
  brokers: wire.brokers.map((row) => ({
    code: row.code,
    name: brokerDisplayName(row.code, row.name),
    rating: row.rating,
    turnover: row.turnover,
    netFlow: row.net_flow,
    trades: row.trades,
    avgTrade: row.avg_trade,
    marketSharePct: row.market_share_pct,
    buySellRatio: row.buy_sell_ratio,
    stocksTraded: row.stocks_traded,
    tradeSizes: row.trade_sizes,
  })),
  best: {
    turnover: wire.best.turnover,
    netFlow: wire.best.net_flow,
    trades: wire.best.trades,
    avgTrade: wire.best.avg_trade,
    marketShare: wire.best.market_share,
    buySellRatio: wire.best.buy_sell_ratio,
    stocks: wire.best.stocks,
    rating: wire.best.rating,
  },
})

export const mapStocksInCommon = (wire: Wire<'StocksInCommon'>) => ({
  window: mapWindow(wire),
  brokers: wire.brokers,
  count: wire.count,
  stocks: wire.stocks.map((row) => ({
    symbol: row.symbol,
    name: stockDisplayName(row.symbol, row.name),
  })),
})

// ── The endpoints ────────────────────────────────────────────────────────────

export const brokerService = {
  /** Overview: the market tiles and the two ranked top-broker lists. */
  overview: async (period: BrokerPeriod): Promise<BrokerOverview> => {
    const data = await getData<Wire<'BrokerOverview'>>(() =>
      apiClient.GET('/api/v5/brokers/overview/', { params: { query: { period } } }),
    )
    return brokerOverviewSchema.parse(mapOverview(data))
  },

  /**
   * Overview's "Stocks traded most": the symbols the market put the most money
   * through, ranked by value traded. Precomputed-only, like sector flow.
   */
  stocksTradedMost: async (period: BrokerPeriod, limit: number): Promise<StocksTradedMost> => {
    const data = await getData<Wire<'StocksTradedMost'>>(() =>
      apiClient.GET('/api/v5/brokers/stocks-traded-most/', {
        params: { query: { period, limit } },
      }),
    )
    return stocksTradedMostSchema.parse(mapStocksTradedMost(data))
  },

  /** All brokers: everyone, by turnover, with the lean. Server-paged. */
  list: async (query: BrokerListQuery): Promise<TablePage<BrokerListRow>> => {
    const { rows, meta } = await getPage<Wire<'BrokerListRow'>>(() =>
      apiClient.GET('/api/v5/brokers/', {
        params: {
          query: {
            page: query.page,
            page_size: query.pageSize,
            period: query.period,
            sort: query.sort,
            ...(query.q === undefined ? {} : { q: query.q }),
          },
        },
      }),
    )
    return listPageSchema.parse(serverPage(rows.map(mapListRow), meta, query))
  },

  /**
   * Most traded: the stocks that churned most for their size. Server-paged,
   * and the one paged read that also reports its own window — so Most traded
   * can print what session the page covers instead of captioning a month of
   * chrome with nothing behind it.
   */
  busyNames: async (query: BusyNamesQuery): Promise<WindowedTablePage<BusyName>> => {
    const { rows, meta } = await getPage<Wire<'BusyName'>>(() =>
      apiClient.GET('/api/v5/brokers/busy-names/', {
        params: {
          query: {
            page: query.page,
            page_size: query.pageSize,
            period: query.period,
            ...(query.sector === undefined ? {} : { sector: query.sector }),
          },
        },
      }),
    )
    return busyNamePageSchema.parse({
      ...serverPage(rows.map(mapBusyName), meta, query),
      window: mapListWindow(meta),
    })
  },

  /** Net broker money per sector. Precomputed-only — it can be empty when nothing else is. */
  sectorFlow: async (period: BrokerPeriod): Promise<SectorFlow> => {
    const data = await getData<Wire<'SectorFlow'>>(() =>
      apiClient.GET('/api/v5/brokers/sector-flow/', { params: { query: { period } } }),
    )
    return sectorFlowSchema.parse(mapSectorFlow(data))
  },

  /**
   * Sector flow's "Broker by sector" grid: the top `limit` brokers by money
   * traded, each with its net in every sector. Precomputed-only, like sector
   * flow.
   */
  brokersBySector: async (period: BrokerPeriod, limit: number): Promise<BrokersBySector> => {
    const data = await getData<Wire<'BrokersBySector'>>(() =>
      apiClient.GET('/api/v5/brokers/by-sector/', { params: { query: { period, limit } } }),
    )
    return brokersBySectorSchema.parse(mapBrokersBySector(data))
  },

  /** One sector's brokers, ranked by net money. */
  sectorBrokers: async (sector: string, period: BrokerPeriod): Promise<SectorBrokers> => {
    const data = await getData<Wire<'SectorBrokers'>>(() =>
      apiClient.GET('/api/v5/brokers/sector-flow/{sector}/', {
        params: { path: { sector }, query: { period } },
      }),
    )
    return sectorBrokersSchema.parse(mapSectorBrokers(data))
  },

  /**
   * Large trades: tiles and the trade list in ONE payload.
   *
   * A plain object read, not `getPage`, because the paging is inside `data`
   * rather than in `meta` — the tiles describe the whole day while `trades` is
   * one page of it, and the backend keeps both together so they cannot
   * disagree. The page therefore comes back on `LargeTrades`, not as a
   * `TablePage`.
   */
  largeTrades: async (query: LargeTradesQuery): Promise<LargeTrades> => {
    const data = await getData<Wire<'LargeTrades'>>(() =>
      apiClient.GET('/api/v5/brokers/large-trades/', {
        params: {
          query: {
            page: query.page,
            page_size: query.pageSize,
            size: query.size,
            ...(query.stock === undefined ? {} : { stock: query.stock }),
            ...(query.broker === undefined ? {} : { broker: query.broker }),
            ...(query.date === undefined ? {} : { date: query.date }),
          },
        },
      }),
    )
    return largeTradesSchema.parse(mapLargeTrades(data))
  },

  /** Brokers most active in big trades. A short list — the backend does not page it. */
  largeTradeLeaders: async (query: LargeTradeLeadersQuery): Promise<LargeTradeLeader[]> => {
    const { rows } = await getPage<Wire<'LargeTradeLeader'>>(() =>
      apiClient.GET('/api/v5/brokers/large-trades/leaders/', {
        params: {
          query: {
            size: query.size,
            ...(query.stock === undefined ? {} : { stock: query.stock }),
          },
        },
      }),
    )
    return rows.map((row) => largeTradeLeaderSchema.parse(mapLargeTradeLeader(row)))
  },

  /**
   * Large holdings: the biggest net broker positions, as a share of the
   * company's paid-up capital.
   *
   * `getData`, not `getPage`, for the same reason `largeTrades` is: the paging
   * lives on `LargeHoldings` itself because the view returns a plain
   * `Response`. Sending `page_size` is what makes the numbers on the pager
   * mean anything — the backend's own default is 50, not the app's 20.
   */
  largeHoldings: async (query: LargeHoldingsQuery): Promise<LargeHoldings> => {
    const data = await getData<Wire<'LargeHoldings'>>(() =>
      apiClient.GET('/api/v5/brokers/large-holdings/', {
        params: {
          query: {
            page: query.page,
            page_size: query.pageSize,
            period: query.period,
            ...(query.sector === undefined ? {} : { sector: query.sector }),
          },
        },
      }),
    )
    return largeHoldingsSchema.parse(mapLargeHoldings(data))
  },

  /**
   * PV range: where one stock's volume actually traded, in Rs 1 price buckets.
   *
   * `symbol` is positional and a PATH parameter; the two optional broker codes
   * and the period are the query. Unpaged on purpose — the profile is the whole
   * window, and there is no `page` on the endpoint to send.
   */
  pvRange: async (symbol: string, query: PVRangeQuery): Promise<PVRange> => {
    const data = await getData<Wire<'PVRange'>>(() =>
      apiClient.GET('/api/v5/brokers/pv-range/{symbol}/', {
        params: {
          path: { symbol },
          query: {
            period: query.period,
            ...(query.buyer === undefined ? {} : { buyer: query.buyer }),
            ...(query.seller === undefined ? {} : { seller: query.seller }),
          },
        },
      }),
    )
    return pvRangeSchema.parse(mapPVRange(data))
  },

  /**
   * Compare: 2–4 brokers side by side, plus who wins each row.
   *
   * The array becomes the wire's comma-separated `brokers` here and nowhere
   * else, so the app never has to think in a delimited string.
   */
  compare: async (brokers: readonly string[], period: BrokerPeriod): Promise<Compare> => {
    const data = await getData<Wire<'Compare'>>(() =>
      apiClient.GET('/api/v5/brokers/compare/', {
        params: { query: { brokers: formatBrokerCodes(brokers), period } },
      }),
    )
    return compareSchema.parse(mapCompare(data))
  },

  /** The symbols every compared broker traded. */
  stocksInCommon: async (
    brokers: readonly string[],
    period: BrokerPeriod,
  ): Promise<StocksInCommon> => {
    const data = await getData<Wire<'StocksInCommon'>>(() =>
      apiClient.GET('/api/v5/brokers/compare/stocks-in-common/', {
        params: { query: { brokers: formatBrokerCodes(brokers), period } },
      }),
    )
    return stocksInCommonSchema.parse(mapStocksInCommon(data))
  },

  /** By stock: which brokers bought and sold this name most. */
  byStock: async (
    symbol: string,
    period: BrokerPeriod,
    range: BrokerDateRange | null = null,
  ): Promise<BrokerByStock> => {
    const data = await getData<Wire<'BrokerByStock'>>(() =>
      apiClient.GET('/api/v5/brokers/by-stock/{symbol}/', {
        // A custom range wins over `period` server-side; sending both would only
        // be a second statement of the window.
        params: {
          path: { symbol },
          query: range === null ? { period } : { start: range.start, end: range.end },
        },
      }),
    )
    return brokerByStockSchema.parse(mapByStock(data))
  },

  /** By broker: the whole page — header, KPIs, flow, style and all six stock blocks. */
  detail: async (code: string, period: BrokerPeriod): Promise<BrokerDetail> => {
    const data = await getData<Wire<'BrokerDetail'>>(() =>
      apiClient.GET('/api/v5/brokers/{code}/', {
        params: { path: { code }, query: { period } },
      }),
    )
    return brokerDetailSchema.parse(mapDetail(data))
  },

  /** Every stock that broker traded — searchable, sortable, server-paged. */
  stocks: async (code: string, query: BrokerStocksQuery): Promise<TablePage<BrokerStockFlow>> => {
    const { rows, meta } = await getPage<Wire<'BrokerStockFlow'>>(() =>
      apiClient.GET('/api/v5/brokers/{code}/stocks/', {
        params: {
          path: { code },
          query: {
            page: query.page,
            page_size: query.pageSize,
            period: query.period,
            sort: query.sort,
            ...(query.q === undefined ? {} : { q: query.q }),
          },
        },
      }),
    )
    return stockFlowPageSchema.parse(serverPage(rows.map(mapStockFlow), meta, query))
  },

  /**
   * EVERY stock the broker traded in the window, not the first page of them.
   *
   * ── Why this exists beside `stocks` ─────────────────────────────────────
   *
   * Compare's "Stocks in common" tab has no endpoint for the rows it draws.
   * `/compare/stocks-in-common/` answers the INTERSECTION only, so the
   * "traded by some of them" rows are diffed client-side from each broker's own
   * list (`screens/brokers/components/compare/compare-stocks.ts`). That diff
   * used to be handed ONE page of 100 per broker, and the tab then counted
   * "traded by both" / "by some of them only" over the capped assembly and
   * sized its pager from it. A broker that traded 300 names contributed its
   * largest hundred, so a name three of four brokers traded could read as two
   * of four — and nothing in the two legend numbers said which set they
   * described.
   *
   * A diff is the one shape where a page is not a smaller version of the
   * answer; it is a different answer. So this reads the whole list.
   *
   * ── The paging is real, so walking it is safe ───────────────────────────
   *
   * `/brokers/{code}/stocks/` merges `PageMeta`
   * (mm-be api/v5/brokers/views.py:215 → services/broker/stocks.py:59, where
   * `total` is `len(rows)` taken BEFORE the slice), so `total_pages` from page 1
   * is authoritative. Page 1 goes first because that is how the count becomes
   * known, then the rest together rather than one after another — the same walk
   * `watchlistService.listAllStocks` already runs.
   *
   * `page_size` is the endpoint's own ceiling of 200
   * (`BrokerStocksRequestSerializer`, api/v5/brokers/serializers.py:40), so
   * against NEPSE's ~300-name universe a broker is one or two requests, and
   * four compared brokers are four to eight. The rows are unsliced and
   * unsorted here on purpose: the caller diffs a SET, and `sort` only decides
   * which rows a page would have held.
   */
  allStocks: async (
    code: string,
    query: { period: BrokerPeriod; sort: BrokerStockSort },
  ): Promise<BrokerStockFlow[]> => {
    const stocksPage = (page: number) =>
      getPage<Wire<'BrokerStockFlow'>>(() =>
        apiClient.GET('/api/v5/brokers/{code}/stocks/', {
          params: {
            path: { code },
            query: {
              page,
              page_size: ALL_STOCKS_PAGE_SIZE,
              period: query.period,
              sort: query.sort,
            },
          },
        }),
      )

    const first = await stocksPage(1)
    const totalPages = first.meta?.total_pages ?? 1
    const rest =
      totalPages > 1
        ? await Promise.all(
            Array.from({ length: totalPages - 1 }, (_, index) => stocksPage(index + 2)),
          )
        : []

    return brokerStockFlowSchema
      .array()
      .parse([first, ...rest].flatMap((page) => page.rows).map(mapStockFlow))
  },

  /** Its raw contracts today, from its own side. Server-paged. */
  floorsheet: async (code: string, query: BrokerTradesQuery): Promise<TablePage<BrokerTrade>> => {
    const { rows, meta } = await getPage<Wire<'BrokerTrade'>>(() =>
      apiClient.GET('/api/v5/brokers/{code}/floorsheet/', {
        params: { path: { code }, query: { page: query.page, page_size: query.pageSize } },
      }),
    )
    return tradePageSchema.parse(serverPage(rows.map(mapTrade), meta, query))
  },

  /** The brokers ranked just above and below it. */
  peers: async (code: string): Promise<BrokerPeer[]> => {
    const { rows } = await getPage<Wire<'BrokerPeer'>>(() =>
      apiClient.GET('/api/v5/brokers/{code}/peers/', { params: { path: { code } } }),
    )
    return rows.map((row) => brokerPeerSchema.parse(mapPeer(row)))
  },

  /** Rating summary + one page of reviews. Public. */
  reviews: async (code: string, query: BrokerReviewsQuery): Promise<BrokerReviews> => {
    const data = await getData<Wire<'BrokerReviews'>>(() =>
      apiClient.GET('/api/v5/brokers/{code}/reviews/', {
        params: { path: { code }, query: { page: query.page, page_size: query.pageSize } },
      }),
    )
    return brokerReviewsSchema.parse(mapReviews(data))
  },

  /**
   * The one write in the whole feature. Authenticated, and an UPSERT: one
   * review per user per broker, so a second submission edits the first. The
   * backend answers 201 on create and 200 on edit with the same body, which is
   * why this returns the review rather than a created/edited flag.
   *
   * The draft is parsed before the request, not after: 1–5 and a non-empty
   * body are the backend's own limits (`ReviewCreateSerializer`), and spending
   * a round trip to be told a slider produced 0 helps nobody.
   */
  createReview: async (code: string, draft: ReviewDraft): Promise<BrokerReview> => {
    const body = reviewDraftSchema.parse(draft)
    const data = await getData<Wire<'Review'>>(() =>
      apiClient.POST('/api/v5/brokers/{code}/reviews/', {
        params: { path: { code } },
        body: { rating: body.rating, review: body.review },
      }),
    )
    return brokerReviewSchema.parse(mapReview(data))
  },
}
